/**
 * One-click template switcher for the Archive modal.
 *
 * The rejection draft is auto-picked from the candidate's stage, which is wrong for cases the stage
 * can't express — most of all a no-show, where the interview-thanks copy is simply untrue. Rather
 * than make a recruiter hand-delete paragraphs, this lets them swap the whole email for another
 * Library template in one click (e.g. "Candidate unresponsive"). It re-renders server-side for THIS
 * candidate (tokens filled), exactly like the language toggle.
 */
import { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from '@finn-auto/design-system/atoms/select';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { fetchEmailTemplates, type EmailTemplate } from '../../services/emailTemplatesApi';
import * as api from '../../services/taActionBoardApi';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { EmailDraft } from '../../types/taActionBoard';

/**
 * Which Library templates the Archive modal offers. All the rejection families, plus the two
 * "candidate is gone" checking-in templates — but NOT the rebooking/housekeeping checking-in ones
 * (reschedule, booking-reminder, …), which make no sense as the email attached to an archive.
 */
const SWITCHER_CHECKING_IN_KEYS = new Set(['candidate-unresponsive', 'closing-loop']);
export function isArchiveSwitchableTemplate(t: EmailTemplate): boolean {
  if (!t.active || t.scope !== 'company') return false;
  if (t.category === 'rejection' || t.category === 'rejection-later' || t.category === 'talent-pool') {
    return true;
  }
  return t.category === 'checking-in' && SWITCHER_CHECKING_IN_KEYS.has(t.key);
}

export function ArchiveTemplateSwitcher({
  opportunityId,
  language,
  disabled,
  hasEdits,
  onSwitched,
}: {
  opportunityId: string;
  language: 'de' | 'en';
  disabled: boolean;
  /** When the recruiter has typed into the body, confirm before replacing it. */
  hasEdits: boolean;
  /** Hand the re-rendered draft back so the modal swaps body + subject in place. */
  onSwitched: (draft: EmailDraft) => void;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [choice, setChoice] = useState('');
  const [switching, setSwitching] = useState(false);

  // Best-effort load: if it fails the switcher just doesn't render, and the suggested template +
  // language toggle still work.
  useEffect(() => {
    let cancelled = false;
    void fetchEmailTemplates()
      .then((all) => {
        if (cancelled) return;
        setTemplates(
          all.filter(isArchiveSwitchableTemplate).sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (templates.length === 0) return null;

  async function switchTo(value: string) {
    const [category, key] = value.split('::');
    if (switching || !category || !key) return;
    if (
      hasEdits &&
      !window.confirm('Switching the template replaces the email with the new one and discards your edits. Continue?')
    ) {
      return;
    }
    setSwitching(true);
    try {
      const next = await api.previewRejectionEmail(opportunityId, language, { category, key });
      setChoice(value);
      onSwitched(next);
      void logAnalyticsEvent('ta_archive_template_switched', { category, key });
    } catch {
      showToast({ status: 'error', description: 'Could not switch the email template.' });
    } finally {
      setSwitching(false);
    }
  }

  return (
    <Select value={choice} onValueChange={switchTo} disabled={disabled || switching}>
      <SelectTrigger label="Template" className="w-full">
        <SelectValue placeholder={switching ? 'Switching…' : 'Suggested template — switch to another…'} />
      </SelectTrigger>
      <SelectContent>
        {templates.map((t) => (
          <SelectItem key={t.id} value={`${t.category}::${t.key}`}>
            {t.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
